import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { useOrders } from '../hooks/useOrders'
import { Download, Loader2 } from 'lucide-react'

const statusColour: Record<string, string> = {
  pending: 'bg-yellow-500/10 text-yellow-600',
  awaiting_payment: 'bg-orange-500/10 text-orange-600',
  processing: 'bg-blue-500/10 text-blue-600',
  fulfilling: 'bg-purple-500/10 text-purple-600',
  completed: 'bg-green-500/10 text-green-600',
  delivered: 'bg-green-700/10 text-green-700',
  cancelled: 'bg-red-500/10 text-red-600',
}

export function OrdersPage() {
  const [vertical, setVertical] = useState<string | undefined>()
  const [exporting, setExporting] = useState(false)
  const { data, isLoading, error } = useOrders({ vertical })

  const orders = data?.orders ?? []

  const handleExport = async () => {
    setExporting(true)
    try {
      const params = new URLSearchParams({ format: 'csv' })
      if (vertical) params.set('vertical', vertical)
      const res = await fetch(`/api/v1/orders/export?${params}`)
      if (!res.ok) throw new Error('Export failed')
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `sendbot-orders-${new Date().toISOString().slice(0, 10)}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Export failed:', err)
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="container max-w-5xl py-8">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Orders</h1>
          <p className="text-muted-foreground">View and track all your orders.</p>
        </div>
        {orders.length > 0 && (
          <Button variant="outline" onClick={handleExport} disabled={exporting}>
            {exporting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Download className="mr-2 h-4 w-4" />
            )}
            Export CSV
          </Button>
        )}
      </div>

      <Tabs
        value={vertical ?? 'all'}
        onValueChange={(v) => setVertical(v === 'all' ? undefined : v)}
        className="mb-6"
      >
        <TabsList>
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="letters">Letters</TabsTrigger>
          <TabsTrigger value="wine">Wine</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card>
        <CardHeader>
          <CardTitle>Order History</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : error ? (
            <p className="text-sm text-destructive">{error.message}</p>
          ) : orders.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              No orders yet. <Link to="/dashboard/send/letter" className="text-primary hover:underline">Send your first letter</Link>.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order ID</TableHead>
                  <TableHead>Recipient</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Cost</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((order) => (
                  <TableRow key={order.id}>
                    <TableCell>
                      <Link
                        to={`/dashboard/orders/${order.id}`}
                        className="font-mono text-sm text-primary hover:underline"
                      >
                        {order.id.slice(0, 12)}...
                      </Link>
                    </TableCell>
                    <TableCell>{order.toName}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" className={statusColour[order.status] ?? ''}>
                        {order.status.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      ${(order.costCents / 100).toFixed(2)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(order.createdAt).toLocaleDateString('en-AU')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
